import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import {getEpiData} from '../../Utils/epiHelpers';
import { getAnimalGenderLabel, getAnimalTypeLabel, getChildGenderLabel } from '../../DataHelpers/lfDataHelpers';

import { fetchCustomerInfo, postNewContactInfo } from './Actions/customerInfoActions';
import { fetchCustomerChildren, updateCustomerChild } from './Actions/customerChildrenActions';
import { fetchCustomerAnimals } from './Actions/customerAnimalsActions';

import Accordion from './components/Accordion/Accordion';
import AccordionItem from './components/Accordion/AccordionItem';
import ChildInfo from './components/ChildInfo';
import AnimalInfo from './components/AnimalInfo';
import PageLoader from './components/PageLoader';
import NoInsurances from './components/NoInsurances';

import ContactInfo from './components/Sections/ContactInfo';
import CoInsuredInfo from './components/Sections/CoInsuredInfo';
import InsuredChildrenInfo from './Components/Sections/InsuredChildrenInfo';
import AnimalsInfo from './Components/Sections/AnimalsInfo';


class CustomerInfoPage extends React.Component {
    static get propTypes() {
        return {
            mountRootId: PropTypes.string.isRequired
        };
    }

    constructor(props) {
        super(props);
        this.attributeData = this.getAttributeData();

        this.state = {
            editContactInfo: false,
            editChildIds: []
        }
    }

    componentDidMount() {
        this.props.loadData();
    }

    getAttributeData() {
        return {
            heading: getEpiData('heading', this.props.mountRootId),
            preamble: getEpiData('preamble', this.props.mountRootId),
            customerSupportUrl: getEpiData('customer-support-url', this.props.mountRootId),
            animalInsuranceSignupUrl: getEpiData('animal-insurance-signup-url', this.props.mountRootId),
            childInsuranceSignupUrl: getEpiData('child-insurance-signup-url', this.props.mountRootId),
        };
    }

    getAnimalElements() {
        const customerSupportUrl = this.attributeData.customerSupportUrl;
        const customerAnimals = this.props.customerAnimals.animals;

        if (!customerAnimals || !customerAnimals.length) { 
            return (<NoInsurances>
                Du har inga försäkringar för djur
            </NoInsurances>);
        }


        return customerAnimals.map((animal, index) => {
            return (
                <AnimalInfo
                    key={index}
                    firstName={animal.animalDisplayName}
                    species={getAnimalTypeLabel(animal.animalType)}
                    breed={animal.animalBreedName}
                    sex={getAnimalGenderLabel(animal.animalGender)}
                    dateOfBirth={animal.animalBirthDate}
                    insuranceNumber={animal.insuranceObjectId}
                    customerSupportUrl={customerSupportUrl}
                />
            );
        })
    }
    getInsuredChildrenElements() {
        const customerSupportUrl = this.attributeData.customerSupportUrl;
        const customerChildren = this.props.customerChildren.children;

        if (!customerChildren || !customerChildren.length) { 
            return (<NoInsurances>
                Du har inga försäkringar för barn
            </NoInsurances>);
        }

        return customerChildren.map((child) => {
            const insuranceId = child.insuranceObjectId;
            const isBeingEdited = this.state.editChildIds.indexOf(insuranceId) !== -1
            const personalIdentityNumberIsEditable = child.personalIdentityNumberIsEditable;
            const firstNameIsEditable = child.firstNameIsEditable;

            return (
                <ChildInfo
                    key={insuranceId}
                    firstName={child.childFirstName}
                    lastName={child.childLastName}
                    gender={getChildGenderLabel(child.childGender)}
                    personalIdentityNumber={child.childPersonalIdentityNumber}
                    dateOfBirth={child.childBirthDate}
                    customerSupportUrl={customerSupportUrl}

                    personalIdentityNumberIsEditable={personalIdentityNumberIsEditable}
                    firstNameIsEditable={firstNameIsEditable}

                    isEditing={isBeingEdited}
                    onEditSave={(firstName, personalIdentityNumber) => {
                        this.updateChild(child, firstName, personalIdentityNumber);
                        this.stopEditChild(insuranceId);
                    }}
                    onEditCancel={() => {
                        this.stopEditChild(insuranceId);
                    }}
                    onEditButtonClick={() => {
                        this.startEditChild(insuranceId);
                    }}

                />
            );
        })
    }

    updateChild(child, firstName, personalIdentityNumber) {
        const formData = {
            PersonId: this.props.customer.personId,
            ChildInsuranceObjectId: child.insuranceObjectId,
        };

        if (firstName) {
            formData.FirstName = firstName;
        }

        if (personalIdentityNumber) {
            formData.PersonalIdentityNumber = personalIdentityNumber;
        }

        this.props.updateChild(formData);
    }
    startEditChild(insuranceObjectId) {
        const editChildIds = this.state.editChildIds;
        editChildIds.push(insuranceObjectId);

        this.setState({
            editChildIds: editChildIds
        });
    }
    stopEditChild(insuranceObjectId) {
        const editChildIds = this.state.editChildIds;
        var childIndex = editChildIds.indexOf(insuranceObjectId);
        
        if (childIndex > -1) {
            editChildIds.splice(childIndex, 1);
        }

        this.setState({
            editChildIds: editChildIds
        });
    }

    formatZipCode(zip) {
        if (!zip || zip.toString().length != 5) {
            // Unknown zip format, do nothing
            return zip;
        }

        zip = zip.toString();

        return zip.substring(0, 3) + ' ' + zip.substring(3);
    }

    render() {
        const title = this.attributeData.heading;
        const preamble = this.attributeData.preamble;
        const customerSupportUrl = this.attributeData.customerSupportUrl;
        const animalInsuranceSignupUrl = this.attributeData.animalInsuranceSignupUrl;
        const childInsuranceSignupUrl = this.attributeData.childInsuranceSignupUrl;

        let coinsuredDescription = "Medförsäkrad är alltid den person som du är gift eller sambo med.";

        if (this.props.coInsuredInfo.personalIdentityNumber)
            coinsuredDescription += " Kontrollera att uppgifterna stämmer.";

        return (
            <Accordion
                title={title}
                htmlDescription={preamble}
            >
                <AccordionItem title="Kontaktuppgifter">
                    <PageLoader
                        isLoading={this.props.customerInfo.fetching}
                    >
                        <ContactInfo
                            name={this.props.customerInfo.firstName + ' ' + this.props.customerInfo.lastName}
                            phoneNumber={this.props.customerInfo.homePhone}
                            mobileNumber={this.props.customerInfo.mobilePhone}
                            adressLine1={this.props.customerInfo.streetAddress}
                            adressLine2={this.formatZipCode(this.props.customerInfo.zipCode) + ' ' + this.props.customerInfo.city}
                            email={this.props.customerInfo.emailAddress}
                            customerSupportUrl={customerSupportUrl}
                            editing={this.state.editContactInfo}
                            onInfoSave={(formData) => {
                                this.props.updateContactInfo(formData);
                                this.setState({ editContactInfo: false });
                            }}
                            onInfoCancel={() => {
                                this.setState({ editContactInfo: false });
                            }}
                            onChangeInfoClick={() => {
                                this.setState({ editContactInfo: true });
                            }}
                        />
                    </PageLoader>
                </AccordionItem>
                <AccordionItem title="Medförsäkrad">
                    <PageLoader
                        isLoading={this.props.coInsuredInfo.fetching}
                    >
                        <CoInsuredInfo
                            title="Medförsäkrad"
                            hasRegisteredInsurances={this.props.coInsuredInfo.isInsured}
                            description={coinsuredDescription}
                            name={this.props.coInsuredInfo.firstName + ' ' + this.props.coInsuredInfo.lastName}
                            personalIdentityNumber={this.props.coInsuredInfo.personalIdentityNumber}
                            customerSupportUrl={customerSupportUrl}
                        />
                    </PageLoader>
                </AccordionItem>
                {/*<AccordionItem title="Barn">*/}
                {/*    <PageLoader*/}
                {/*        isLoading={this.props.customerChildren.fetching}*/}
                {/*    >*/}
                {/*        <InsuredChildrenInfo*/}
                {/*            title="Barn"*/}
                {/*            description="Om du har eller snart ska få barn kan du försäkra dem genom Lärarförsäkringar. Kontrollera gärna att uppgifterna stämmer."*/}
                {/*            addButtonUrl={childInsuranceSignupUrl}*/}
                {/*        >*/}
                {/*            {this.getInsuredChildrenElements()}*/}
                {/*        </InsuredChildrenInfo>*/}
                {/*    </PageLoader>*/}
                {/*</AccordionItem>*/}
                {/*<AccordionItem title="Husdjur">*/}
                {/*    <PageLoader*/}
                {/*        isLoading={this.props.customerAnimals.fetching}*/}
                {/*    >*/}
                {/*        <AnimalsInfo*/}
                {/*            title="Husdjur"*/}
                {/*            description="Om du har husdjur kan du försäkra dem genom Lärarförsäkringar. Kontrollera gärna att uppgifterna stämmer."*/}
                {/*            addButtonUrl={animalInsuranceSignupUrl}*/}
                {/*        >*/}
                {/*            {this.getAnimalElements()}*/}
                {/*        </AnimalsInfo>*/}
                {/*    </PageLoader>*/}
                {/*</AccordionItem>*/}
            </Accordion>
        );
    }

}

const mapStateToProps = (state) => {
    return {
        customerInfo: state.customerInfoPageCustomerInfo,
        coInsuredInfo: state.customerInfoPageCoInsuredInfo,
        //customerAnimals: state.customerInfoPageCustomerAnimals,
        //customerChildren: state.customerInfoPageCustomerChildren,
        hasErrored: state.pageHasErrored,
        error: state.pageError,
        isLoading: state.pageIsLoading,
        step: state.pageCurrentStep
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadData: () => {
            dispatch(fetchCustomerInfo())
           // dispatch(fetchCustomerChildren())
           // dispatch(fetchCustomerAnimals())
        },
        updateContactInfo: (formData) => {
            dispatch(postNewContactInfo(formData));
        },
        updateChild: (formData) => {
            dispatch(updateCustomerChild(formData));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerInfoPage);